import { Fragment, useEffect, useState } from 'react'
import Box from '@mui/material/Box'
import Collapse from '@mui/material/Collapse'
import Divider from '@mui/material/Divider'
import Fade from '@mui/material/Fade'
import ListItemButton from '@mui/material/ListItemButton'
import ListItemIcon from '@mui/material/ListItemIcon'
import ListItemText from '@mui/material/ListItemText'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import useMediaQuery from '@mui/material/useMediaQuery'
import { alpha } from '@mui/material/styles'
import { ChevronDown, PanelLeftClose, PanelLeftOpen } from 'lucide-react'
import { Link as RouterLink, NavLink, useLocation } from 'react-router-dom'
import { portalNav, type NavItem, type NavSection, type NavSubgroup } from '../../core/router/navConfig'
import { areaPorChave } from '../../core/areas/areas'
import { useAreaAtual } from '../../core/areas/useAreas'
import { AreaSwitcher } from './AreaSwitcher'
import { useAuth } from '../../core/auth/useAuth'
import { useCan } from '../../core/auth/useCan'
import { useI18n } from '../../core/i18n/useI18n'
import { Leaf } from '../leaf/LeafDefs'
import { LEAF_RADIUS_SM, EASE_ORGANIC, brand } from '../../styles/theme'

// Estado de expansão v2: só ESCOLHAS MANUAIS entram aqui. O aberto efetivo é
// `manual[grupo] ?? contémRotaAtiva(grupo)` — default fechado, exceto os
// ancestrais da rota ativa. A chave antiga (nav-open-sections, default aberto)
// tem semântica incompatível e é descartada na primeira carga.
const OPEN_KEY = 'nav-open-groups.v2'
const LEGACY_OPEN_KEY = 'nav-open-sections'

function readManual(): Record<string, boolean> {
  try {
    localStorage.removeItem(LEGACY_OPEN_KEY)
    return JSON.parse(localStorage.getItem(OPEN_KEY) || '{}')
  } catch {
    return {}
  }
}

function persistManual(manual: Record<string, boolean>) {
  try {
    localStorage.setItem(OPEN_KEY, JSON.stringify(manual))
  } catch {
    /* ignore */
  }
}

const leafSx = {
  position: 'relative',
  zIndex: 0,
  overflow: 'hidden',
  borderRadius: LEAF_RADIUS_SM,
  mb: 0.25,
  minHeight: 40,
  color: 'text.secondary',
  transition: `color 180ms ${EASE_ORGANIC}`,
  // Folha desabrochando: o verde da seleção é revelado por clip-path (esquerda → direita),
  // recortado na silhueta-folha (borderRadius + overflow hidden). O ::before fica em z:-1
  // (atrás do ícone/label, à frente do fundo), então o hover mostra action.hover.
  '&::before': {
    content: '""',
    position: 'absolute',
    inset: 0,
    zIndex: -1,
    // Verde sutil: preenchimento ativo bem discreto (marca "folha" sem gritar).
    backgroundColor: alpha(brand.floresta, 0.07),
    clipPath: 'inset(0 100% 0 0)',
    transition: `clip-path 260ms ${EASE_ORGANIC}`,
  },
  '& .MuiListItemIcon-root': { transition: `color 150ms, transform 180ms ${EASE_ORGANIC}` },
  '&:hover': { bgcolor: 'action.hover', color: 'text.primary' },
  '&.Mui-focusVisible': { boxShadow: `inset 0 0 0 2px ${alpha(brand.floresta, 0.4)}` },
  '&.active': {
    color: 'primary.main',
    fontWeight: 600,
    '& .MuiListItemIcon-root': { color: 'primary.main', transform: 'translateY(-1px)' },
    '&::before': { clipPath: 'inset(0 0 0 0)' },
  },
}

/** Item de hub (Cadastros/Categorias) fica ativo em qualquer subrota de `path`,
 *  menos os primeiros segmentos em `matchExclude` (ex.: Conjuntos moram sob
 *  um hub mas têm item próprio). */
function isHubActive(pathname: string, base: string, exclude: string[] = []): boolean {
  if (pathname === base) return true
  if (!pathname.startsWith(base + '/')) return false
  const first = pathname.slice(base.length + 1).split('/')[0]
  return !exclude.includes(first)
}

/** A rota atual "pertence" ao item? Hubs usam a regra do isHubActive; folhas
 *  casam por prefixo (subrotas como /duplas/new mantêm o grupo pai aberto). */
function isItemInPath(pathname: string, item: NavItem): boolean {
  if (item.matchNested) return isHubActive(pathname, item.path, item.matchExclude)
  return pathname === item.path || pathname.startsWith(item.path + '/')
}

function subgroupId(sectionId: string, sub: NavSubgroup): string {
  return `${sectionId}/${sub.title}`
}

function domId(groupId: string): string {
  return `nav-group-${groupId.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`
}

function NavLeaf({ item, collapsed, onNavigate }: { item: NavItem; collapsed?: boolean; onNavigate?: () => void }) {
  const Icon = item.icon
  const { t } = useI18n()
  const label = item.labelKey ? t(item.labelKey) : item.label
  const { pathname } = useLocation()
  const nested = Boolean(item.matchNested)
  const nestedActive = nested && isHubActive(pathname, item.path, item.matchExclude)
  const sx = { ...leafSx, justifyContent: collapsed ? 'center' : 'flex-start', px: collapsed ? 0 : 1.5 }
  const inner = (
    <>
      <ListItemIcon sx={{ minWidth: collapsed ? 0 : 32, color: 'inherit', justifyContent: 'center' }}>
        <Icon size={18} strokeWidth={2} />
      </ListItemIcon>
      {!collapsed && (
        <ListItemText primary={label} primaryTypographyProps={{ fontSize: '0.875rem', fontWeight: 'inherit' }} />
      )}
      {!collapsed && !item.ready && (
        <Box
          component="span"
          sx={{ fontSize: '0.625rem', fontWeight: 600, letterSpacing: '0.04em', color: 'text.disabled', border: '1px solid', borderColor: 'divider', borderRadius: 1, px: 0.5, py: 0.1 }}
        >
          {t('nav.comingSoon')}
        </Box>
      )}
    </>
  )
  // Hubs: casam por prefixo (activo calculado à mão via RouterLink + classe .active).
  // Demais itens: NavLink com match exato (`end`).
  const button = nested ? (
    <ListItemButton component={RouterLink} to={item.path} onClick={onNavigate} className={nestedActive ? 'active' : undefined} sx={sx}>
      {inner}
    </ListItemButton>
  ) : (
    <ListItemButton component={NavLink} to={item.path} onClick={onNavigate} end sx={sx}>
      {inner}
    </ListItemButton>
  )
  return collapsed ? (
    <Tooltip
      title={label}
      placement="right"
      arrow
      slotProps={{
        tooltip: {
          sx: {
            bgcolor: 'background.paper',
            color: 'text.primary',
            fontSize: '0.8125rem',
            fontWeight: 600,
            px: 1.5,
            py: 0.75,
            ml: 0.5,
            borderRadius: LEAF_RADIUS_SM,
            border: '1px solid',
            borderColor: 'divider',
            boxShadow: '0 8px 24px rgba(20,40,18,0.16)',
          },
        },
        arrow: { sx: { color: (t) => t.palette.background.paper } },
      }}
    >
      {button}
    </Tooltip>
  ) : (
    button
  )
}

/** Chevron único que gira (fechado = -90°). `reduced` desliga a transição. */
function GroupChevron({ open, reduced, size = 14 }: { open: boolean; reduced: boolean; size?: number }) {
  return (
    <ChevronDown
      size={size}
      style={{
        transform: open ? 'none' : 'rotate(-90deg)',
        transition: reduced ? undefined : `transform 200ms ${EASE_ORGANIC}`,
      }}
      aria-hidden
    />
  )
}

function SectionHeader({ title, open, reduced, regionId, onClick }: {
  title: string
  open: boolean
  reduced: boolean
  regionId: string
  onClick: () => void
}) {
  return (
    <ListItemButton
      onClick={onClick}
      aria-expanded={open}
      aria-controls={regionId}
      sx={{
        borderRadius: 2, mt: 2.5, mb: 0.5, py: 0.5, px: 1.5, color: 'text.disabled',
        '&:hover': { bgcolor: 'action.hover' },
        '&.Mui-focusVisible': { boxShadow: `inset 0 0 0 2px ${alpha(brand.floresta, 0.4)}` },
      }}
    >
      <Typography
        component="span"
        sx={{ flex: 1, fontSize: '0.6875rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'inherit' }}
      >
        {title}
      </Typography>
      <GroupChevron open={open} reduced={reduced} />
    </ListItemButton>
  )
}

function SubgroupHeader({ title, open, reduced, regionId, onClick }: {
  title: string
  open: boolean
  reduced: boolean
  regionId: string
  onClick: () => void
}) {
  return (
    <ListItemButton
      onClick={onClick}
      aria-expanded={open}
      aria-controls={regionId}
      sx={{
        borderRadius: LEAF_RADIUS_SM, mt: 0.5, mb: 0.25, minHeight: 34, py: 0.25, px: 1.5,
        color: 'text.secondary',
        '&:hover': { bgcolor: 'action.hover', color: 'text.primary' },
        '&.Mui-focusVisible': { boxShadow: `inset 0 0 0 2px ${alpha(brand.floresta, 0.4)}` },
      }}
    >
      <Typography component="span" sx={{ flex: 1, fontSize: '0.8125rem', fontWeight: 600, color: 'inherit' }}>
        {title}
      </Typography>
      <GroupChevron open={open} reduced={reduced} size={13} />
    </ListItemButton>
  )
}

/** Corpo animado de um grupo: altura (Collapse) + opacidade (Fade na entrada). */
function GroupBody({ open, reduced, regionId, labelId, children }: {
  open: boolean
  reduced: boolean
  regionId: string
  labelId?: string
  children: React.ReactNode
}) {
  return (
    <Collapse in={open} timeout={reduced ? 0 : 'auto'} unmountOnExit>
      <Fade in appear={!reduced} timeout={reduced ? 0 : 220}>
        <Box id={regionId} role="group" aria-labelledby={labelId}>
          {children}
        </Box>
      </Fade>
    </Collapse>
  )
}

type VisibleSection = NavSection & { subgroups: NavSubgroup[] }

/** Conteúdo da sidebar (drawer permanente + mobile). Nav conforme o papel.
 *  `collapsed` = modo mini (ícones + folha no lugar da logo).
 *
 *  Equipe, desde as áreas (24/10/2026): só a navegação da área atual, tirada do
 *  endereço, com o seletor de área no topo para quem tem mais de uma. */
export function SidebarContent({ collapsed = false, onNavigate, onToggleCollapse }: { collapsed?: boolean; onNavigate?: () => void; onToggleCollapse?: () => void }) {
  const { user } = useAuth()
  const { t } = useI18n()
  const can = useCan()
  const { pathname } = useLocation()
  const reduced = useMediaQuery('(prefers-reduced-motion: reduce)')
  const isStaff = Boolean(user?.is_staff)
  const isCustomerAdmin = user?.role === 'customer_admin'
  // Backoffice e portal são ambos NavSection[]; itens adminOnly só p/ o admin do
  // cliente; itens com `permission` só se o usuário tiver a permissão (view).
  // O filtro vale também para subgrupos: subgrupo sem itens some; seção sem
  // itens E sem subgrupos some (ex.: Jornada sem lodging.view).
  const visible = (item: NavItem) =>
    (!item.adminOnly || isCustomerAdmin) &&
    (!item.permission || can(item.permission)) &&
    (!item.anyPermission || item.anyPermission.some((key) => can(key)))
  const areaAtual = useAreaAtual()
  const navDaArea: NavSection[] = areaAtual ? areaPorChave(areaAtual).nav : []
  // O estado aberto de cada grupo leva a área na chave: duas áreas podem ter seções
  // de mesmo título, e abrir uma não pode abrir a outra.
  const grupoId = (title: string) => (isStaff && areaAtual ? `${areaAtual}/${title}` : title)
  const sections: VisibleSection[] = (isStaff ? navDaArea : portalNav)
    .map((s) => ({
      ...s,
      items: s.items.filter(visible),
      subgroups: (s.subgroups ?? [])
        .map((g) => ({ ...g, items: g.items.filter(visible) }))
        .filter((g) => g.items.length > 0),
    }))
    .filter((s) => s.items.length > 0 || s.subgroups.length > 0)

  const [manual, setManual] = useState<Record<string, boolean>>(readManual)

  const sectionContainsRoute = (s: VisibleSection) =>
    s.items.some((i) => isItemInPath(pathname, i)) ||
    s.subgroups.some((g) => g.items.some((i) => isItemInPath(pathname, i)))
  const subgroupContainsRoute = (g: NavSubgroup) => g.items.some((i) => isItemInPath(pathname, i))

  const isOpen = (id: string, containsRoute: boolean) => manual[id] ?? containsRoute

  // Expansão automática dos ancestrais da rota ativa: quem estava recolhido À
  // MÃO volta ao automático (a entrada some) e abre. O usuário ainda pode
  // recolher de novo depois — só não fica com a rota ativa escondida ao navegar.
  useEffect(() => {
    setManual((prev) => {
      const ancestrais: string[] = []
      for (const s of sections) {
        if (sectionContainsRoute(s)) ancestrais.push(grupoId(s.title))
        for (const g of s.subgroups) {
          if (subgroupContainsRoute(g)) ancestrais.push(subgroupId(grupoId(s.title), g))
        }
      }
      const fechados = ancestrais.filter((id) => prev[id] === false)
      if (fechados.length === 0) return prev
      const next = { ...prev }
      for (const id of fechados) delete next[id]
      persistManual(next)
      return next
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname])

  function toggle(id: string, containsRoute: boolean) {
    setManual((prev) => {
      const next = { ...prev, [id]: !isOpenWith(prev, id, containsRoute) }
      persistManual(next)
      return next
    })
  }

  function isOpenWith(state: Record<string, boolean>, id: string, containsRoute: boolean) {
    return state[id] ?? containsRoute
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', bgcolor: 'background.paper' }}>
      <Box sx={{ height: 64, display: 'flex', alignItems: 'center', justifyContent: collapsed ? 'center' : 'flex-start', px: collapsed ? 1 : 2.5, flex: 'none' }}>
        {collapsed ? (
          <Box sx={{ color: 'primary.main', display: 'flex' }} aria-label="HubTransGrãos">
            <Leaf style={{ height: 18, width: 'auto' }} />
          </Box>
        ) : (
          <Box component="img" src="/assets/logo-hubtransgraos.webp" alt="HubTransGrãos" sx={{ height: 26, width: 'auto' }} />
        )}
      </Box>

      {isStaff && areaAtual && <AreaSwitcher atual={areaAtual} collapsed={collapsed} onNavigate={onNavigate} />}

      <Box component="nav" sx={{ px: collapsed ? 1 : 1.5, py: 1, flex: 1, overflowY: 'auto', overflowX: 'hidden' }}>
        {sections.map((section) => {
          const secId = grupoId(section.title)
          const secOpen = isOpen(secId, sectionContainsRoute(section))
          const secRegion = domId(secId)
          return (
            <Fragment key={section.title}>
              {collapsed ? (
                <>
                  {/* Modo mini: lista plana (itens diretos + subgrupos na sequência),
                      com tooltip à direita; grupos não se aplicam recolhido. */}
                  {section.items.map((item) => (
                    <NavLeaf key={item.path} item={item} collapsed onNavigate={onNavigate} />
                  ))}
                  {section.subgroups.flatMap((g) => g.items).map((item) => (
                    <NavLeaf key={item.path} item={item} collapsed onNavigate={onNavigate} />
                  ))}
                  <Divider sx={{ my: 0.75 }} />
                </>
              ) : (
                <>
                  <SectionHeader
                    title={section.titleKey ? t(section.titleKey) : section.title}
                    open={secOpen}
                    reduced={reduced}
                    regionId={secRegion}
                    onClick={() => toggle(secId, sectionContainsRoute(section))}
                  />
                  <GroupBody open={secOpen} reduced={reduced} regionId={secRegion}>
                    {section.items.map((item) => (
                      <NavLeaf key={item.path} item={item} onNavigate={onNavigate} />
                    ))}
                    {section.subgroups.map((sub) => {
                      const id = subgroupId(secId, sub)
                      const subOpen = isOpen(id, subgroupContainsRoute(sub))
                      const subRegion = domId(id)
                      return (
                        <Fragment key={sub.title}>
                          <SubgroupHeader
                            title={sub.titleKey ? t(sub.titleKey) : sub.title}
                            open={subOpen}
                            reduced={reduced}
                            regionId={subRegion}
                            onClick={() => toggle(id, subgroupContainsRoute(sub))}
                          />
                          <GroupBody open={subOpen} reduced={reduced} regionId={subRegion}>
                            {/* Recuo do 2º nível: trilho no divider + indent. */}
                            <Box sx={{ ml: 2.25, pl: 1, borderLeft: '1px solid', borderColor: 'divider' }}>
                              {sub.items.map((item) => (
                                <NavLeaf key={item.path} item={item} onNavigate={onNavigate} />
                              ))}
                            </Box>
                          </GroupBody>
                        </Fragment>
                      )
                    })}
                  </GroupBody>
                </>
              )}
            </Fragment>
          )
        })}
      </Box>

      <Box sx={{ px: collapsed ? 1 : 1.5, py: 1, flex: 'none', borderTop: '1px solid', borderColor: 'divider' }}>
        {onToggleCollapse && (
          <Tooltip
            title={collapsed ? t('nav.expand') : ''}
            placement="right"
            arrow
            disableHoverListener={!collapsed}
          >
            <ListItemButton
              onClick={onToggleCollapse}
              aria-label={collapsed ? t('nav.expand') : t('nav.collapse')}
              sx={{ ...leafSx, mb: 0.75, justifyContent: collapsed ? 'center' : 'flex-start', px: collapsed ? 0 : 1.5 }}
            >
              <ListItemIcon sx={{ minWidth: collapsed ? 0 : 32, color: 'inherit', justifyContent: 'center' }}>
                {collapsed ? <PanelLeftOpen size={18} strokeWidth={2} /> : <PanelLeftClose size={18} strokeWidth={2} />}
              </ListItemIcon>
              {!collapsed && <ListItemText primary={t('nav.collapse')} primaryTypographyProps={{ fontSize: '0.8125rem', fontWeight: 'inherit' }} />}
            </ListItemButton>
          </Tooltip>
        )}
        <Typography variant="caption" sx={{ color: 'text.disabled', display: 'block', textAlign: collapsed ? 'center' : 'left', px: collapsed ? 0 : 0.5 }}>
          {collapsed ? '© 2026' : '© 2026 TransGrãos LTDA'}
        </Typography>
      </Box>
    </Box>
  )
}
